import { useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  CheckCircle2,
  CircleAlert,
  Clock3,
  CreditCard,
  Loader2,
  LockKeyhole,
  Phone,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { formatPrice } from "@/lib/format";
import { storefrontSdk } from "@/lib/sdk";
import {
  clearPaymentLinkReturnContext,
  writePaymentLinkReturnContext,
} from "@/lib/payment-link-return-context";

export const Route = createFileRoute("/pay/$token")({
  component: PaymentLinkPage,
});

function PaymentLinkPage() {
  const { token } = Route.useParams();
  const { t, lang } = useI18n();
  const paymentLink = useQuery({
    queryKey: ["public-payment-link", token],
    queryFn: () => storefrontSdk.paymentLink(token),
    refetchInterval: (query) =>
      query.state.data?.status === "awaiting_payment" ? 2_000 : false,
  });

  useEffect(() => {
    const meta = document.querySelector<HTMLMetaElement>(
      'meta[name="referrer"]',
    );
    const previous = meta?.content;
    if (meta) meta.content = "no-referrer";
    return () => {
      if (meta && previous !== undefined) meta.content = previous;
    };
  }, []);

  useEffect(() => {
    const status = paymentLink.data?.status;
    if (status && status !== "awaiting_payment") {
      clearPaymentLinkReturnContext(window.sessionStorage);
    }
  }, [paymentLink.data?.status]);

  if (paymentLink.isLoading) {
    return (
      <PaymentPageMessage icon="loading" title={t("paymentLink.loading")} />
    );
  }
  if (paymentLink.isError || !paymentLink.data) {
    return (
      <PaymentPageMessage
        icon="error"
        title={t("paymentLink.notFoundTitle")}
        body={t("paymentLink.notFoundBody")}
        action={
          <Button variant="outline" onClick={() => void paymentLink.refetch()}>
            {t("common.retry")}
          </Button>
        }
      />
    );
  }

  const data = paymentLink.data;
  const restaurantName =
    data.restaurant.name[lang] ?? data.restaurant.name["en"] ?? "";
  const total = formatPrice(data.order.amountMinor, lang, data.order.currency);

  function continueToProvider() {
    if (!data.checkout.available || !data.checkout.url) return;
    writePaymentLinkReturnContext(window.sessionStorage, {
      token,
      startedAt: new Date().toISOString(),
    });
    window.location.assign(data.checkout.url);
  }

  return (
    <div className="pattern-levant min-h-screen bg-background px-4 py-8 sm:py-14">
      <main className="mx-auto w-full max-w-xl">
        <section className="surface-card overflow-hidden" aria-live="polite">
          <div className="border-b bg-primary/[0.04] px-6 py-6 text-center sm:px-8">
            {data.restaurant.logoUrl ? (
              <img
                src={data.restaurant.logoUrl}
                alt=""
                className="mx-auto size-16 rounded-2xl object-cover shadow-sm"
              />
            ) : (
              <span className="mx-auto grid size-16 place-items-center rounded-2xl bg-primary text-2xl font-black text-primary-foreground shadow-sm">
                {restaurantName.slice(0, 1).toUpperCase()}
              </span>
            )}
            <p className="mt-3 text-sm font-semibold text-muted-foreground">
              {restaurantName}
            </p>
            <h1 className="mt-1 text-2xl font-black tracking-tight">
              {t("paymentLink.title")}
            </h1>
          </div>

          <div className="space-y-5 px-6 py-6 sm:px-8 sm:py-8">
            <div className="grid grid-cols-2 gap-3 rounded-2xl border bg-muted/30 p-4">
              <PaymentFact
                label={t("paymentLink.orderNumber")}
                value={data.order.number}
              />
              <PaymentFact
                label={t("paymentLink.customer")}
                value={data.order.customerName}
              />
              <div className="col-span-2 border-t pt-3 text-center">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t("paymentLink.total")}
                </p>
                <p className="mt-1 text-3xl font-black tabular-nums">{total}</p>
              </div>
            </div>

            <PaymentOrderReview order={data.order} />

            <PaymentStatus status={data.status} />

            {data.status === "awaiting_payment" ? (
              <Button
                type="button"
                size="lg"
                className="min-h-12 w-full gap-2 rounded-xl text-base"
                disabled={!data.checkout.available}
                onClick={continueToProvider}
              >
                {data.checkout.available ? (
                  <CreditCard className="size-5" />
                ) : (
                  <Loader2 className="size-5 animate-spin" />
                )}
                {data.checkout.available
                  ? t("paymentLink.payNow", { total })
                  : t("paymentLink.preparing")}
              </Button>
            ) : null}

            <div className="space-y-2 border-t pt-4 text-xs leading-5 text-muted-foreground">
              <p className="flex items-start gap-2">
                <LockKeyhole className="mt-0.5 size-4 shrink-0 text-primary" />
                {t("paymentLink.secureHelp")}
              </p>
            </div>

            <a
              href={`tel:${data.restaurant.supportPhone}`}
              className="flex items-center justify-center gap-2 text-sm font-semibold text-primary hover:underline"
              dir="ltr"
            >
              <Phone className="size-4" />
              {data.restaurant.supportPhone}
            </a>
          </div>
        </section>
        <p className="mt-4 text-center text-xs text-muted-foreground">
          {t("footer.powered")}
        </p>
      </main>
    </div>
  );
}

type PaymentOrder = Awaited<
  ReturnType<typeof storefrontSdk.paymentLink>
>["order"];

function PaymentOrderReview({ order }: { order: PaymentOrder }) {
  const { t, lang } = useI18n();
  const money = (minor: number) => formatPrice(minor, lang, order.currency);
  const localized = (value: Record<string, string>) =>
    value[lang] ?? value["en"] ?? value["ar"] ?? "";
  const address = order.address
    ? [
        order.address.street,
        order.address.building,
        order.address.floor,
        order.address.apartment,
        order.address.area,
        order.address.landmark,
      ]
        .filter(Boolean)
        .join(", ")
    : "";

  return (
    <section aria-labelledby="payment-order-review" className="space-y-4">
      <h2 id="payment-order-review" className="text-lg font-bold">
        {t("paymentLink.reviewOrder")}
      </h2>
      <dl className="divide-y rounded-xl border bg-muted/20 px-4 text-sm">
        <ReviewDetail
          label={t("paymentLink.branch")}
          value={localized(order.branchName)}
        />
        <ReviewDetail
          label={t("paymentLink.fulfillment")}
          value={
            order.orderType === "delivery"
              ? t("paymentLink.delivery")
              : t("paymentLink.pickup")
          }
        />
        <ReviewDetail
          label={t("paymentLink.requestedTime")}
          value={
            order.fulfillment.mode === "scheduled"
              ? new Intl.DateTimeFormat(lang, {
                  dateStyle: "medium",
                  timeStyle: "short",
                }).format(new Date(order.fulfillment.scheduledFor))
              : t("paymentLink.asap")
          }
        />
        {address ? (
          <ReviewDetail label={t("paymentLink.address")} value={address} />
        ) : null}
        {order.address?.instructions ? (
          <ReviewDetail
            label={t("paymentLink.deliveryInstructions")}
            value={order.address.instructions}
          />
        ) : null}
        {order.notes ? (
          <ReviewDetail label={t("paymentLink.notes")} value={order.notes} />
        ) : null}
      </dl>

      <div className="divide-y rounded-xl border">
        {order.items.map((item, index) => (
          <div
            key={`${index}-${localized(item.name)}`}
            className="flex gap-3 p-3"
          >
            {item.imageUrl ? (
              <img
                src={item.imageUrl}
                alt=""
                loading="lazy"
                className="size-16 shrink-0 rounded-lg object-cover"
              />
            ) : null}
            <div className="min-w-0 flex-1">
              <p className="font-semibold" dir="auto">
                {localized(item.name)}{" "}
                <span className="whitespace-nowrap font-medium text-muted-foreground">
                  × {item.quantity}
                </span>
              </p>
              {item.variantName ? (
                <p className="text-xs text-muted-foreground" dir="auto">
                  {localized(item.variantName)}
                </p>
              ) : null}
              {item.addons.length ? (
                <p className="text-xs text-muted-foreground" dir="auto">
                  {item.addons
                    .map((addon) => localized(addon.name))
                    .join(" · ")}
                </p>
              ) : null}
              {item.notes ? (
                <p className="mt-1 text-xs text-muted-foreground" dir="auto">
                  {item.notes}
                </p>
              ) : null}
              <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <p className="text-xs text-muted-foreground">
                  {money(item.unitPriceMinor)} {t("paymentLink.each")}
                </p>
                <span className="ms-auto text-sm font-semibold tabular-nums">
                  {money(item.lineTotalMinor)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <dl className="space-y-2 border-t pt-4 text-sm">
        <ReviewTotal
          label={t("paymentLink.subtotal")}
          value={money(order.pricing.subtotalMinor)}
        />
        {order.pricing.discountMinor > 0 ? (
          <ReviewTotal
            label={t("paymentLink.discount")}
            value={`−${money(order.pricing.discountMinor)}`}
          />
        ) : null}
        {order.orderType === "delivery" ? (
          <ReviewTotal
            label={t("paymentLink.deliveryFee")}
            value={money(order.pricing.deliveryFeeMinor)}
          />
        ) : null}
        {order.pricing.serviceFeeMinor > 0 ? (
          <ReviewTotal
            label={t("paymentLink.serviceFee")}
            value={money(order.pricing.serviceFeeMinor)}
          />
        ) : null}
        {order.pricing.tipMinor > 0 ? (
          <ReviewTotal
            label={t("paymentLink.tip")}
            value={money(order.pricing.tipMinor)}
          />
        ) : null}
        <div className="flex justify-between border-t pt-3 text-base font-bold">
          <dt>{t("paymentLink.total")}</dt>
          <dd className="tabular-nums">{money(order.amountMinor)}</dd>
        </div>
      </dl>
    </section>
  );
}

function ReviewDetail({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[minmax(0,35%)_minmax(0,1fr)] items-start gap-x-3 py-3 leading-5 sm:grid-cols-[minmax(0,10rem)_minmax(0,1fr)] sm:gap-x-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words text-start font-semibold">
        <bdi>{value}</bdi>
      </dd>
    </div>
  );
}

function ReviewTotal({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}

function PaymentStatus({
  status,
}: {
  status:
    | "awaiting_payment"
    | "paid"
    | "failed"
    | "expired"
    | "cancelled"
    | "review_required";
}) {
  const { t } = useI18n();
  const configuration = {
    awaiting_payment: {
      icon: Clock3,
      title: t("paymentLink.awaitingTitle"),
      body: t("paymentLink.awaitingBody"),
      className: "border-primary/25 bg-primary/5 text-primary",
    },
    paid: {
      icon: CheckCircle2,
      title: t("paymentLink.paidTitle"),
      body: t("paymentLink.paidBody"),
      className: "border-success/30 bg-success/10 text-success",
    },
    failed: {
      icon: CircleAlert,
      title: t("paymentLink.failedTitle"),
      body: t("paymentLink.failedBody"),
      className: "border-destructive/30 bg-destructive/5 text-destructive",
    },
    expired: {
      icon: Clock3,
      title: t("paymentLink.expiredTitle"),
      body: t("paymentLink.expiredBody"),
      className: "border-border bg-muted/40 text-foreground",
    },
    cancelled: {
      icon: CircleAlert,
      title: t("paymentLink.cancelledTitle"),
      body: t("paymentLink.cancelledBody"),
      className: "border-border bg-muted/40 text-foreground",
    },
    review_required: {
      icon: Clock3,
      title: t("paymentLink.reviewTitle"),
      body: t("paymentLink.reviewBody"),
      className: "border-warning/40 bg-warning/10 text-foreground",
    },
  }[status];
  const Icon = configuration.icon;
  return (
    <div
      className={`flex items-start gap-3 rounded-xl border p-4 ${configuration.className}`}
    >
      <Icon className="mt-0.5 size-5 shrink-0" />
      <div>
        <p className="text-sm font-bold">{configuration.title}</p>
        <p className="mt-1 text-xs leading-5 opacity-80">
          {configuration.body}
        </p>
      </div>
    </div>
  );
}

function PaymentFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 text-center">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 truncate text-sm font-bold" dir="auto">
        {value}
      </p>
    </div>
  );
}

function PaymentPageMessage({
  icon,
  title,
  body,
  action,
}: {
  icon: "loading" | "error";
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <main className="grid min-h-screen place-items-center bg-background px-4">
      <section className="surface-card w-full max-w-md p-8 text-center">
        {icon === "loading" ? (
          <Loader2 className="mx-auto size-9 animate-spin text-primary" />
        ) : (
          <CircleAlert className="mx-auto size-9 text-destructive" />
        )}
        <h1 className="mt-4 text-xl font-black">{title}</h1>
        {body ? (
          <p className="mt-2 text-sm text-muted-foreground">{body}</p>
        ) : null}
        {action ? <div className="mt-5">{action}</div> : null}
      </section>
    </main>
  );
}
